import React from 'react';
import { Link } from 'react-router-dom';
import styles from '../styles/Packages.module.css';
import { Helmet } from 'react-helmet-async';

const packages = [
    {
        number: '01',
        name: 'landing page',
        subtitle: 'single-page site for launches, campaigns, and personal projects',
        price: 'starting at $300',
        timeline: '1-2 weeks',
        pages: '1 page',
        description:
            'A focused one-page website for people who need a clean, credible online presence without committing to a larger build.',
        includes: [
            '1 custom landing page',
            'responsive front-end build',
            'basic section structure',
            'light content placement',
            'contact or call-to-action section',
            'launch support',
        ],
        excludes:
            'domain, hosting, copywriting, brand identity work, advanced integrations, and major custom functionality',
        fit: 'good for artists, campaigns, events, early-stage brands, and single-offer businesses',
    },
    {
        number: '02',
        name: 'starter site',
        subtitle: 'small multi-page website for creators and small organizations',
        price: 'starting at $1,500',
        timeline: '2-4 weeks',
        pages: '3-5 pages',
        description:
            'A clear, custom site build for people who need more than a landing page but do not need a large or complex website.',
        includes: [
            'discovery and planning',
            'site structure and page mapping',
            '3 to 5 custom pages',
            'responsive front-end build',
            'contact form setup',
            'light content placement',
            'launch support',
        ],
        excludes:
            'copywriting, advanced CMS builds, custom integrations, heavy animation, and ongoing monthly support',
        fit: 'good for portfolios, service businesses, creative studios, and community organizations',
    },
    {
        number: '03',
        name: 'full site',
        subtitle: 'custom website for brands that need a fuller digital presence',
        price: 'starting at $3,200',
        timeline: '4-6 weeks',
        pages: '6-10 pages',
        description:
            'A fuller website build for brands, platforms, and organizations that need more content, more structure, and a stronger overall web presence.',
        includes: [
            'discovery and strategy call',
            'site architecture',
            '6 to 10 custom pages',
            'responsive front-end build',
            'standard form or platform integrations',
            'content placement',
            'launch support',
        ],
        excludes:
            'ongoing support retainer, copywriting, advanced app features, and large-volume content migration',
        fit: 'good for active brands, editorial projects, growing platforms, and organizations with multiple priorities',
    },
    {
        number: '04',
        name: 'ongoing support',
        subtitle: 'monthly website support after launch',
        price: 'starting at $250/month',
        timeline: 'monthly',
        pages: '1 small update per month',
        description:
            'A light support option for clients who need routine help after launch without opening a new project every time.',
        includes: [
            '1 small content or layout update per month',
            'minor bug fixes',
            'troubleshooting',
            'technical guidance by email',
        ],
        excludes:
            'full redesigns, new page builds, rebrands, major feature work, and unused hours rolling over',
        fit: 'good for clients who need occasional maintenance and light ongoing help',
    },
    {
        number: '05',
        name: 'technical tune-up',
        subtitle: 'front-end fixes, cleanup, and implementation help',
        price: 'starting at $300',
        timeline: 'project-based',
        pages: 'existing site support',
        description:
            'For existing sites that need cleanup, bug fixes, layout corrections, or technical help without a full rebuild.',
        includes: [
            'front-end fixes',
            'layout cleanup',
            'bug investigation',
            'implementation help',
            'technical consulting',
        ],
        excludes:
            'full redesigns, full rebuilds, and open-ended monthly development unless scoped separately',
        fit: 'good for sites with bugs, design issues, cleanup needs, or technical debt',
    },
    {
        number: '06',
        name: 'strategy + story',
        subtitle: 'development support for essays, digital series, and story-led projects',
        price: 'custom quote',
        timeline: 'project-based',
        pages: 'not page-based',
        description:
            'Support for story-led projects that need stronger framing before production or launch. This work focuses on structure, concept, and positioning.',
        includes: [
            'concept development',
            'story framing',
            'project positioning',
            'format development',
            'preproduction planning',
            'pitch and deck shaping',
        ],
        excludes:
            'production, full writing services, and full campaign execution unless scoped separately',
        fit: 'good for documentaries, essays, digital series, branded storytelling, and culture-led media projects',
    },
];

export default function Packages() {
    return (
        <>
            <Helmet>
                <title>Packages | Meshae Studios</title>
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>
            <main className={styles.packagesPage}>
                <section className={styles.heroSection}>
                    <div className={styles.pageGrid}>
                        <div className={styles.heroMeta}>
                            <p className={styles.kicker}>services / packages</p>
                            <p className={styles.micro}>meshae studios</p>
                        </div>

                        <div className={styles.heroTitleBlock}>
                            <h1 className={styles.heroTitle}>services and support</h1>
                        </div>

                        <div className={styles.heroCopyBlock}>
                            <p className={styles.heroLead}>
                                Clear ways to work together across websites, technical support, and story-led development.
                            </p>

                            <p className={styles.heroDescription}>
                                These packages are designed to be straightforward. If you need something outside of these scopes, we can shape a custom project based on your goals, timeline, and budget.
                            </p>
                        </div>
                    </div>
                </section>

                <section className={styles.packagesSection}>
                    <div className={styles.pageGrid}>
                        <div className={styles.sectionMarker}>
                            <span className={styles.kicker}>current offers</span>
                        </div>

                        <div className={styles.packagesColumn}>
                            {packages.map((item) => (
                                <article key={item.number} className={styles.packageRow}>
                                    <div className={styles.packageTopLine}>
                                        <p className={styles.packageNumber}>{item.number}</p>
                                        <div className={styles.rule} />
                                    </div>

                                    <div className={styles.packageGrid}>
                                        <div className={styles.packageNameBlock}>
                                            <h2 className={styles.packageName}>{item.name}</h2>
                                            <p className={styles.packageSubtitle}>{item.subtitle}</p>
                                            <p className={styles.packagePrice}>{item.price}</p>
                                        </div>

                                        <div className={styles.packageBodyBlock}>
                                            <p className={styles.packageDescription}>
                                                {item.description}
                                            </p>

                                            <div className={styles.packageStats}>
                                                <div>
                                                    <p className={styles.metaLabel}>pages / scope</p>
                                                    <p className={styles.statText}>{item.pages}</p>
                                                </div>

                                                <div>
                                                    <p className={styles.metaLabel}>timeline</p>
                                                    <p className={styles.statText}>{item.timeline}</p>
                                                </div>
                                            </div>

                                            <div className={styles.packageMetaGrid}>
                                                <div className={styles.metaBlock}>
                                                    <p className={styles.metaLabel}>includes</p>
                                                    <ul className={styles.includesList}>
                                                        {item.includes.map((line) => (
                                                            <li key={line}>{line}</li>
                                                        ))}
                                                    </ul>
                                                </div>

                                                <div className={styles.metaBlock}>
                                                    <p className={styles.metaLabel}>good fit for</p>
                                                    <p className={styles.fitText}>{item.fit}</p>
                                                </div>

                                                <div className={styles.metaBlock}>
                                                    <p className={styles.metaLabel}>not included</p>
                                                    <p className={styles.excludesText}>{item.excludes}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className={styles.alacarteSection}>
                    <div className={styles.pageGrid}>
                        <div className={styles.alacarteLabelBlock}>
                            <p className={styles.kicker}>a la carte</p>
                        </div>

                        <div className={styles.alacarteContent}>
                            <h2 className={styles.alacarteTitle}>hourly support</h2>
                            <p className={styles.alacarteText}>
                                Not every project needs a full package. If you just need a fix, short consultation, or a small piece of implementation work, hourly support is available for one-off needs.
                            </p>

                            <a
                                href="https://www.upwork.com/freelancers/iamsdawson"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.inlineLink}
                            >
                                book hourly support on upwork
                            </a>
                        </div>
                    </div>
                </section>

                <section className={styles.ctaSection}>
                    <div className={styles.pageGrid}>
                        <div className={styles.ctaLabelBlock}>
                            <p className={styles.kicker}>next step</p>
                        </div>

                        <div className={styles.ctaContent}>
                            <h2 className={styles.ctaTitle}>
                                Pick the closest fit, then book a kickoff call.
                            </h2>

                            <div className={styles.ctaLinks}>
                                <a href="/meetings" className={styles.primaryLink}>
                                    book a kickoff call
                                </a>

                                <Link to="/designlab" className={styles.secondaryLink}>
                                    view my work
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
}
